<script lang="ts" setup>
	import useProductActivationApi from '~/services/apis/product-activation'
	import usePurchases from '~/services/apis/purchases-info'
	import type { filterCollectionSchema } from '~/components/Forms/filter.vue'
	import type { IdAndName, RecordSchema } from '@/types'

	const props = defineProps<{
		planDetails: PlanInformationContext | null
		tableUi?: ClassContext
	}>()
	const { plan_id } = useRoute().params
	const { getPurchasePolicies } = usePurchases()
	const { getPendingPolicy } = useProductActivationApi()
	const { productCategoryType } = useUtils()
	const searchVal = shallowRef('')
	const { internationalNoFormat } = useCurrency()
	const { formatDate } = useCustomDate()
	const { categoryType } = usePurchaseInfo()
	const filterList = shallowRef<filterCollectionSchema[]>([
		{
			key: 'active',
			value: 'Active'
		},
		{
			key: 'pending',
			value: 'Pending'
		}
	])
	const filterBy = shallowRef('active')
	const cellClass = 'text-xs text-gray-600 font-medium tracking-wide xl:whitespace-nowrap'

	const tableOptions = computed<TableFilterOptions>(() => ({
		cache: 0,
		filter: {
			search: searchVal.value,
			query: filterBy.value
		},
		pagination: true
	}))
	const tableColumns = computed(() => {
		let list: TableColumn[] = [
			{
				key: 'sn',
				dataIndex: 'sn',
				title: 'S/N',
				className: cellClass,
				width: 50
			}
		]
		if (categoryType.value.isHealth) {
			list = [
				...list,
				{
					key: 'full_name',
					dataIndex: 'full_name',
					title: 'Staff Name',
					className: cellClass
				},
				{
					key: 'dependants',
					dataIndex: 'dependants',
					title: 'Total dependant',
					className: cellClass,
					customCell: () => ({
						class: 'text-center'
					})
				},
				{
					key: 'status',
					dataIndex: 'status',
					title: 'Status',
					className: cellClass,
					width: 80
				}
			]
		} else if (categoryType.value?.isAuto) {
			list = [
				...list,
				{
					key: 'vehicle_type',
					dataIndex: 'vehicle_type',
					title: 'Vehicle Type',
					className: cellClass
				},
				{
					key: 'manufacturer',
					dataIndex: 'manufacturer',
					title: 'Manufacturer',
					className: cellClass,
					customCell: () => ({
						class: 'text-center'
					})
				},
				{
					key: 'vehicle_model',
					dataIndex: 'model',
					title: 'Vehicle Model',
					className: cellClass,
					customCell: () => ({
						class: 'text-center'
					})
				},
				{
					key: 'value',
					dataIndex: 'value',
					title: 'Value',
					className: cellClass,
					customCell: () => ({
						class: 'text-center'
					})
				}
			]
		} else if (categoryType.value?.isGadget) {
			list = [
				...list,
				{
					key: 'gadget_type',
					dataIndex: 'gadget_type',
					title: 'Gadget Type',
					className: cellClass
				},
				{
					key: 'gadget_brand',
					dataIndex: 'brand',
					title: 'Brand',
					className: cellClass
				},
				{
					key: 'gadget_model',
					dataIndex: 'model',
					title: 'Model',
					className: cellClass
				},
				{
					key: 'serial_number',
					dataIndex: 'serial_number',
					title: 'Serial Number',
					className: cellClass
				},
				{
					key: 'gadget_value',
					dataIndex: 'value',
					title: 'Value',
					className: cellClass
				}
			]
		} else if (categoryType.value?.isTravel) {
			list = [
				...list,
				{
					key: 'location_name',
					dataIndex: 'location_name',
					title: 'Location',
					className: cellClass
				},
				{
					key: 'start_date',
					dataIndex: 'start_date',
					title: 'Travel Date',
					className: cellClass
				},
				{
					key: 'end_date',
					dataIndex: 'end_date',
					title: 'Return Date',
					className: cellClass
				},
				{
					key: 'first_name',
					dataIndex: 'first_name',
					title: 'First Name',
					className: cellClass
				},
				{
					key: 'last_name',
					dataIndex: 'last_name',
					title: 'Last Name',
					className: cellClass
				},
				{
					key: 'email',
					dataIndex: 'email',
					title: 'Email Address',
					className: cellClass
				},
				{
					key: 'phone_number',
					dataIndex: 'phone_number',
					title: 'Phone Number',
					className: cellClass
				}
			]
		}
		return [
			...list,
			{
				key: 'details',
				dataIndex: 'details',
				title: 'Details',
				className: cellClass,
				width: 50
			}
		]
	})
	const getRecord = async (query: RecordSchema) => {
		if (filterBy.value !== 'pending') {
			const res = await getPurchasePolicies(String(plan_id), query ?? {})
			return res?.data ?? null
		}
		const result = await getPendingPolicy({
			query: {
				purchase_id: String(plan_id),
				...query
			}
		})
		return result
	}

	const getStaff = (id: string) => {
		if (filterBy.value === 'pending') {
			return navigateTo({
				path: `/mycover/custodian/${id}`,
				query: {
					status: 'pending'
				}
			})
		}
		navigateTo(`/mycover/custodian/${id}`)
	}
</script>

<template>
	<div>
		<div class="w-full">
			<div class="mb-5 flex h-[48px] w-full items-center rounded-xl border pr-8 md:py-3">
				<forms-search-field
					v-model:value="searchVal"
					class="mr-3 w-full border-r pl-5"
					:use-custom-class="false"
				/>

				<forms-filter
					v-model:filter-by="filterBy"
					class="flex min-w-[50px] items-center"
					:use-default-class="false"
					:filter-collection="filterList"
				/>
			</div>

			<ui-data-table
				:columns="tableColumns"
				:records="getRecord"
				:options="tableOptions"
				class="mt-16"
				:table-ui="tableUi"
			>
				<template #bodyCell="{ column, record, index }">
					<span v-if="column?.dataIndex === 'full_name'">
						{{ record?.first_name ?? record?.meta?.first_name }}
						{{ record?.last_name ?? record?.meta?.last_name }}
					</span>
					<span v-if="column?.dataIndex === 'first_name'">
						{{ record?.first_name ?? record?.meta?.first_name }}
					</span>
					<span v-if="column?.dataIndex === 'last_name'">
						{{ record?.last_name ?? record?.meta?.last_name }}
					</span>
					<span v-if="column?.dataIndex === 'email'">
						{{ record?.email ?? record?.meta?.email }}
					</span>
					<span v-if="column?.dataIndex === 'phone_number'">
						{{ record?.phone_number ?? record?.meta?.phone_number }}
					</span>
					<span v-if="column?.dataIndex === 'dependants'">
						{{ record?.meta?.no_of_dependant || record?.no_of_dependant || 0 }}
					</span>
					<span v-if="column.dataIndex === 'vehicle_type'">
						{{ (record?.meta?.vehicle_type as IdAndName)?.name || record?.meta?.vehicle_type || 'N/A' }}
					</span>
					<span v-if="column.dataIndex === 'plate_number'">
						{{ record?.meta?.plate_number || record?.meta?.vehicle_registration_number || 'N/A' }}
					</span>

					<span v-if="column.dataIndex === 'manufacturer'">
						{{ (record?.meta?.vehicle_make as IdAndName)?.name || record?.meta?.vehicle_make || 'N/A' }}
					</span>
					<span v-if="column.dataIndex === 'model'">
						{{
							renderItem(
								String(
									(record?.meta?.gadget_model as IdAndName)?.name ||
										record?.meta?.gadget_model ||
										(record?.meta?.vehicle_model as IdAndName)?.name ||
										record?.meta?.vehicle_model ||
										''
								)
							)
						}}
					</span>
					<span v-if="column.dataIndex === 'brand'">
						{{
							renderItem(
								(record?.meta?.gadget_brand as IdAndName)?.name || String(record?.meta?.gadget_brand)
							)
						}}
					</span>
					<span v-if="column.dataIndex === 'gadget_type'">
						{{
							renderItem(
								(record?.meta?.gadget_type as IdAndName)?.name || String(record?.meta?.gadget_type)
							)
						}}
					</span>
					<span v-if="column.dataIndex === 'custodian'">
						{{ record?.first_name || record?.meta?.first_name }}
						{{ record?.last_name || record?.meta?.last_name }}
					</span>
					<span v-if="column.dataIndex === 'location_name'">{{
						record?.meta?.location_name || record?.meta?.destination
					}}</span>
					<span v-if="column.dataIndex === 'end_date'">{{
						formatDate(record?.meta?.end_date || record?.meta?.arrival_date, 'DD/MM/YYYY')
					}}</span>
					<span v-if="column.dataIndex === 'start_date'">{{
						formatDate(record?.meta?.start_date || record?.meta?.departure_date, 'DD/MM/YYYY')
					}}</span>
					<span v-if="column.dataIndex === 'value'">
						{{ internationalNoFormat(record?.vehicle_value || record?.price || 0) }}
					</span>
					<my-cover-plan-status
						v-else-if="column?.key === 'status'"
						:pending="!record?.policy_number"
						:plan-details="record || null"
					/>
					<slot :name="column?.dataIndex" :record="record" :index="index" />
					<button
						v-if="column?.dataIndex === 'details'"
						type="button"
						aria-label="activate staff"
						aria-roledescription="view beneficiary plan details"
						class="text-sm font-medium"
						@click="getStaff(record.id)"
					>
						<span class="font-medium text-primary-2">
							{{ filterBy === 'pending' ? 'Activate' : 'View' }}
						</span>
					</button>
				</template>
			</ui-data-table>
		</div>
	</div>
</template>
